import React, { useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import "./Auth.css";
import { setAuthSession } from "../services/api";
import { useModalA11y } from "../hooks/useModalA11y";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotMessage, setForgotMessage] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();

      if (res.ok) {
        setAuthSession({
          token: data.token,
          refreshToken: data.refreshToken,
          user: data.user,
        });
        navigate("/");
      } else {
        setMessage(data.message || "Login gagal");
      }
    } catch (err) {
      setMessage("Terjadi kesalahan server");
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      setForgotMessage("Masukkan email Anda terlebih dahulu");
      return;
    }

    setForgotLoading(true);
    try {
      const res = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgotEmail }),
      });
      const data = await res.json();

      if (res.ok) {
        setForgotMessage("✅ Email reset password sudah dikirim! Cek inbox Anda");
        setTimeout(() => {
          setShowForgotModal(false);
          setForgotEmail("");
          setForgotMessage("");
        }, 2000);
      } else {
        setForgotMessage(data.message || "Email tidak terdaftar");
      }
    } catch (err) {
      setForgotMessage("Terjadi kesalahan, coba lagi nanti");
    } finally {
      setForgotLoading(false);
    }
  };

  const closeForgotModal = useCallback(() => {
    setShowForgotModal(false);
    setForgotEmail("");
    setForgotMessage("");
  }, []);
  const forgotModalRef = useModalA11y(showForgotModal, closeForgotModal);

  return (
    <div className="auth-page min-h-screen flex items-center justify-center bg-gray-100">
      <form
        onSubmit={handleLogin}
        className="bg-white p-8 rounded-2xl shadow-lg w-96 space-y-4"
      >
        <h2 className="text-2xl font-bold text-center text-blue-600">Login</h2>

        {message && <p className="text-red-500 text-center" role="alert">{message}</p>}

        <label htmlFor="login-email" className="auth-label">Email</label>
        <input
          id="login-email"
          type="email"
          placeholder="Email"
          className="w-full p-2 border rounded-lg"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="email"
        />

        <label htmlFor="login-password" className="auth-label">Password</label>
        <div className="relative">
          <input
            id="login-password"
            type={showPassword ? "text" : "password"}
            placeholder="Password"
            className="w-full p-2 pr-10 border rounded-lg"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-600 hover:text-gray-800 cursor-pointer"
            aria-label={showPassword ? "Sembunyikan kata sandi" : "Tampilkan kata sandi"}
          >
            <span aria-hidden="true">{showPassword ? "👁️" : "👁️‍🗨️"}</span>
          </button>
        </div>

        <button
          type="submit"
          className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
        >
          Masuk
        </button>

        <p className="text-center text-sm">
          Belum punya akun?{" "}
          <Link to="/register" className="text-blue-500 hover:underline">
            Daftar
          </Link>
        </p>

        <div className="text-center">
          <button
            type="button"
            onClick={() => {
              setShowForgotModal(true);
              setForgotMessage("");
            }}
            className="text-blue-500 hover:underline text-sm"
          >
            Lupa Kata Sandi?
          </button>
        </div>
      </form>

      {/* Modal Lupa Password */}
      {showForgotModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={closeForgotModal}
          role="presentation"
        >
          <div
            ref={forgotModalRef}
            className="bg-white p-8 rounded-2xl shadow-lg w-96 space-y-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="forgot-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="forgot-modal-title" className="text-2xl font-bold text-center text-blue-600">
              Lupa Kata Sandi?
            </h2>

            {forgotMessage && (
              <p
                className={`text-center text-sm px-4 py-2 rounded-lg ${
                  forgotMessage.includes("✅")
                    ? "bg-green-100 text-green-700"
                    : "bg-red-100 text-red-700"
                }`}
                role="alert"
              >
                {forgotMessage}
              </p>
            )}

            <p className="text-center text-gray-600 text-sm">
              Masukkan email Anda dan kami akan mengirimkan link untuk reset kata sandi
            </p>

            <form onSubmit={handleForgotPassword} className="space-y-4">
              <label htmlFor="forgot-email" className="auth-label">Email</label>
              <input
                id="forgot-email"
                type="email"
                placeholder="Masukkan email Anda"
                className="w-full p-2 border rounded-lg"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                required
                autoComplete="email"
              />

              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={forgotLoading}
                  className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {forgotLoading ? "Mengirim..." : "Kirim Link Reset"}
                </button>
                <button
                  type="button"
                  data-modal-close
                  onClick={closeForgotModal}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400"
                >
                  Batal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
